import { useState, useEffect } from 'react';

/**
 * Hook which returns the value and the set value function.
 * Use this to add data to local storage.
 *
 * e.g.
 * const [name, setName] = useLocalStorage('name', 'Marie');
 *
 * @param {string} key   - key
 * @param {string|object} initialValue - inital value of the data
 * @returns {[object, Function]} - data and set function received for that key.
 */

const useLocalStorage = (key, initialValue) => {
  // State to store our value
  // Pass initial state function to useState so logic is only executed once
  const [storedValue, setStoredValue] = useState(() => {
    try {
      // Get from local storage by key
      const item = window.localStorage.getItem(key);
      // Parse stored json or if none return initialValue
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      // If error also return initialValue
      return initialValue;
    }
  });
  // Return a wrapped version of useState's setter function that ...
  // ... persists the new value to localStorage immediately (not only in an
  // effect), so a quick refresh after setValue cannot lose the write.
  const setValue = (value) => {
    try {
      setStoredValue((prev) => {
        const valueToStore = value instanceof Function ? value(prev) : value;
        window.localStorage.setItem(key, JSON.stringify(valueToStore));
        return valueToStore;
      });
    } catch (error) {
      // A more advanced implementation would handle the error case
    }
  };

  useEffect(() => {
    window.localStorage.setItem(key, JSON.stringify(storedValue));
  }, [storedValue, key]);
  return [storedValue, setValue];
};

export default useLocalStorage;
